import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import voiceBotService from '../services/VoiceBotService';

export default function VoicePreview({ clientId, voice, greeting, disabled }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [url, setUrl] = useState('');
    const [error, setError] = useState(false);
    const pending = useRef(null);
    const audio = useRef(null);

    useEffect(() => {
        setUrl('');
        setLoading(false);
        setError(false);
        return () => {
            pending.current?.abort();
            pending.current = null;
        };
    }, [clientId, voice, greeting]);

    useEffect(() => {
        const player = audio.current;
        return () => {
            player?.pause();
            if (url) URL.revokeObjectURL(url);
        };
    }, [url]);

    const preview = async () => {
        if (pending.current) return;
        const request = new AbortController();
        pending.current = request;
        audio.current?.pause();
        setUrl('');
        setLoading(true);
        setError(false);
        try {
            const blob = await voiceBotService.preview(clientId, voice, greeting.trim(), request.signal);
            if (request.signal.aborted) return;
            setUrl(URL.createObjectURL(blob));
        } catch (failure) {
            if (!request.signal.aborted) {
                const knownErrors = ['previewQuota', 'previewRateLimit', 'previewLimitUnknown'];
                setError(knownErrors.includes(failure.code) ? failure.code : 'previewError');
            }
        } finally {
            if (pending.current === request) {
                pending.current = null;
                setLoading(false);
            }
        }
    };

    return <Stack spacing={1}>
        <Button variant="outlined" onClick={preview} disabled={disabled || loading || !greeting.trim()}>
            {t(loading ? 'voiceBots.previewLoading' : 'voiceBots.preview')}
        </Button>
        <Typography variant="caption" color="text.secondary">{t('voiceBots.previewHelp')}</Typography>
        {error && <Alert severity="error">{t(`voiceBots.${typeof error === 'string' ? error : 'previewError'}`)}</Alert>}
        {url && <audio ref={audio} src={url} controls autoPlay aria-label={t('voiceBots.preview')} style={{ width: '100%' }}
            onError={() => setError(true)} />}
    </Stack>;
}
